import { GoalStatusCode } from ".";

/**
 * Source-agnostic ROS 2 action surface, mirroring the ServiceCaller pattern.
 * Each backend (roslib, foxglove) provides an implementation; arcjs actions become
 * pluggable like services.
 */
export interface ActionHandle {
  /** The ROS 2 goal id (canonical UUID string) this handle tracks. */
  readonly goalId: string;
  /** Request cancellation of this goal on the action server. */
  cancel(): Promise<void>;
  /** Detach client-side (stop listening); does not cancel the goal on the server. */
  stop(): void;
}

export interface ActionCallbacks<TFeedback = unknown, TResult = unknown> {
  /** Live goal status transitions (GoalStatus enum). */
  onStatus?: (status: GoalStatusCode) => void;
  /** Feedback payload (the FeedbackMessage's `feedback` field). */
  onFeedback?: (feedback: TFeedback) => void;
  /** Terminal success: the result payload plus its terminal status. */
  onResult?: (result: TResult, status: GoalStatusCode) => void;
  /** Terminal failure / rejection / cancel / expiry. */
  onFailed?: (error: string) => void;
}

export abstract class ActionCaller {
  /** Originate a goal we own: generate a goal id, send it, then await the result. */
  abstract sendGoal<TGoal = unknown, TFeedback = unknown, TResult = unknown>(
    actionName: string,
    goal: TGoal,
    callbacks?: ActionCallbacks<TFeedback, TResult>
  ): Promise<ActionHandle>;

  /**
   * Join a goal started elsewhere, identified only by its ROS 2 goal id. Subscribes
   * to the goal's feedback/status and awaits its result, keyed purely on `goalId`.
   */
  abstract monitorGoal<TFeedback = unknown, TResult = unknown>(
    actionName: string,
    goalId: string,
    callbacks?: ActionCallbacks<TFeedback, TResult>
  ): Promise<ActionHandle>;
}

export class ActionNotSupportedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ActionNotSupportedError";
  }
}
